import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../../lib/api.js';
import type { AuditEvent, ResearchItem } from '../../lib/types.js';
import { StatusBadge } from '../../components/StatusBadge.js';

export function ResearchDetailPage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const itemQuery = useQuery({
    queryKey: ['research', id],
    queryFn: () => api.get<ResearchItem>(`/research/${id}`),
  });
  const auditQuery = useQuery({
    queryKey: ['research', id, 'audit'],
    queryFn: () => api.get<AuditEvent[]>(`/research/${id}/audit`),
  });

  const review = useMutation({
    mutationFn: (decision: 'reviewed' | 'rejected') =>
      api.post<ResearchItem>(`/research/${id}/review`, { decision, notes: notes || undefined }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['research', id] });
      await queryClient.invalidateQueries({ queryKey: ['research', id, 'audit'] });
      await queryClient.invalidateQueries({ queryKey: ['research'] });
    },
    onError: (err) => setError(err instanceof ApiClientError ? err.message : 'Review failed'),
  });

  if (itemQuery.isLoading) return <p>Loading…</p>;
  if (itemQuery.isError || !itemQuery.data) {
    return (
      <p role="alert" className="form-error">
        Research item not found.
      </p>
    );
  }

  const item = itemQuery.data.data;

  return (
    <section>
      <p>
        <Link to="/research">← Back to research</Link>
      </p>
      <div className="page-head">
        <h1>{item.title}</h1>
        <StatusBadge status={item.status} />
      </div>

      <dl className="detail-grid">
        <dt>Summary</dt>
        <dd>{item.summary}</dd>
        <dt>Signal</dt>
        <dd>{item.signalType}</dd>
        <dt>Confidence</dt>
        <dd>{Math.round(item.confidence * 100)}%</dd>
        <dt>Uncertainty</dt>
        <dd>{item.uncertainty ?? '—'}</dd>
        <dt>Observed</dt>
        <dd>{new Date(item.observedAt).toLocaleDateString()}</dd>
        <dt>Source</dt>
        <dd>
          <a href={item.sourceUrl} target="_blank" rel="noreferrer noopener">
            {item.sourceDomain}
          </a>
        </dd>
        <dt>Created by</dt>
        <dd>{item.createdBy.actorType}</dd>
      </dl>

      {item.policyNotes && (
        <div className="policy-note" role="note">
          <strong>Policy notes:</strong> {item.policyNotes}
        </div>
      )}

      {item.status === 'draft' ? (
        <div className="review-panel">
          <h2>Review</h2>
          <label htmlFor="notes">Notes</label>
          <textarea id="notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
          {error && (
            <p role="alert" className="form-error">
              {error}
            </p>
          )}
          <div className="button-row">
            <button
              type="button"
              onClick={() => review.mutate('reviewed')}
              disabled={review.isPending}
            >
              Mark reviewed
            </button>
            <button
              type="button"
              className="button--danger"
              onClick={() => review.mutate('rejected')}
              disabled={review.isPending}
            >
              Reject
            </button>
          </div>
        </div>
      ) : (
        item.review && (
          <p className="muted">
            {item.review.decision} on {new Date(item.review.reviewedAt).toLocaleString()}
            {item.review.notes ? ` — ${item.review.notes}` : ''}
          </p>
        )
      )}

      <h2>Audit timeline</h2>
      {auditQuery.data && auditQuery.data.data.length > 0 ? (
        <ul className="audit-list">
          {auditQuery.data.data.map((e) => (
            <li key={e._id}>
              <code>{e.action}</code> by {e.actorType} · {new Date(e.occurredAt).toLocaleString()}
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">No audit events.</p>
      )}
    </section>
  );
}
